import isFunction from 'lodash/isFunction';
import identity from 'lodash/identity';

import { RUMHelpers } from '../datadog';
import { ActionName, Context } from '../types';

export type OnAction = (name: ActionName, context?: Context) => void;
export type OnError<ErrorT extends Error = Error> = (
  error: ErrorT,
  name: ActionName,
  context?: Context,
) => void;
export interface ConsumerFunc<ArgsT extends any[], ResultT> {
  (...args: ArgsT): ResultT;
}

export interface Either<ArgsT extends any[], ResultT> {
  getResult: () => ArgsT | ResultT;
}
export interface Left<ArgsT extends any[], ResultT> extends Either<ArgsT, ResultT> {
  tap: (handler: () => void) => Left<ArgsT, ResultT>;
  mapError: (handleError: any) => Left<ArgsT, ResultT>;
}
export interface Right<ArgsT extends any[], ResultT> extends Either<ArgsT, ResultT> {
  tap: (handler: () => void) => Right<ArgsT, ResultT>;
  map: (handleError: any) => Right<ArgsT, ResultT>;
}
export interface ReporterConfig {
  /** Report only errors */
  errorsOnly?: boolean;
}

export interface Reporter {
  runWithReporting: <ArgsT extends any[], ResultT>(
    name: ActionName,
    errorFunc: ConsumerFunc<ArgsT, ResultT>,
    operationalFunc: ConsumerFunc<ArgsT, ResultT>,
    reporterConfig?: ReporterConfig,
  ) => ConsumerFunc<ArgsT, ResultT>;
}

const getFirstResult = (eitherResult: any[]): any => {
  if (Array.isArray(eitherResult) && eitherResult.length >= 1) {
    return getFirstResult(eitherResult[0]);
  }
  return eitherResult;
};

const createLeftPath = <ArgsT extends any[], ResultT>(
  ...consumerArgs: ArgsT
): Left<ArgsT, ResultT> => ({
  getResult: () => getFirstResult(consumerArgs),
  // @ts-ignore
  mapError: function mapError(handleError: ConsumerFunc<ArgsT, ResultT>) {
    const leftResult = handleError(...consumerArgs);
    const nextLeftPath = createLeftPath(leftResult);
    return nextLeftPath;
  },
  tap: function tap(tapHandler: () => void): Left<ArgsT, ResultT> {
    tapHandler();
    return createLeftPath(...consumerArgs);
  },
});
const createRightPath = <ArgsT extends any[], ResultT>(
  ...consumerArgs: ArgsT
): Right<ArgsT, ResultT> => ({
  getResult: () => getFirstResult(consumerArgs),
  // @ts-ignore
  map: function map(handleError: ConsumerFunc<ArgsT, ResultT>) {
    const rightResult = handleError(...consumerArgs);
    const nextRightPath = createRightPath(rightResult);
    return nextRightPath;
  },
  tap: function tap(tapHandler: () => void) {
    tapHandler();
    return createRightPath(...consumerArgs);
  },
});

/**
 * @description The module is instantiated and configured by providing handler functions ultimately called in the event of a log(successful operation)
 *  and a error(error caught). Once created, the module provides a functions prewrapped with reporting capabilities using the observability
 *  handlers configured.
 * @param onError Observability function invoked on a successful operation
 * @param onAction Observability function invoked on an exception throwing operation
 * @returns {object} Reporter
 */
const createReporter = (onError: OnError, onAction: OnAction): Reporter => {
  if (isFunction(onError) ? !isFunction(onAction) : true) {
    console.error('Observability reporter requires handlers be provided'); // eslint-disable-line
    return { runWithReporting: identity };
  }

  /**
   * @param {ActionName} name ActionName
   * @param {ConsumerFunc<ArgsT>} errorFunc ConsumerFunc - ArgsT typed as an array of arguments
   * @param {ConsumerFunc<ArgsT>} operationalFunc ConsumerFunc - ArgsT typed as an array of arguments
   * @param {ReporterConfig} reporterConfig ReporterConfig - Configuration options for the runner
   * @return {Function} Handler
   * @description This function is a simple wrapper utilizing a try-catch with a layer of abstraction and composability via functions.
   *  You can think of it as a composable tool that enables the creation of linked pathways.
   *  A `Left` path which handles error cases and a `Right`path which handles the normal flow.
   *  A basic use case
   * ```js
   *  // runWithReporting is invoked with functions meant to handle an error and normal flow.
   *  // Note these functions should be written as units of work preferrablly as pure functions.
   *  const handleWork = runWithReporting('Reporting instance Name', functionToHandleError, functionToHandleWork);
   *  // The returned function is called where needed.
   *  handleWork('foobar', sampleObject, onUpdateState);
   *
   * // While not ideal, the result can be used
   * const result = handleWork('foobar', sampleObject, onUpdateState);
   * ```
   */
  const runWithReporting = function runWithReporting<ArgsT extends any[], ResultT>(
    name: ActionName,
    errorFunc: ConsumerFunc<ArgsT, ResultT>,
    operationalFunc: ConsumerFunc<ArgsT, ResultT>,
    reporterConfig?: ReporterConfig,
  ) {
    const { errorsOnly = false } = reporterConfig || {};
    return (...consumerArgs: ArgsT) => {
      const leftPath = createLeftPath<ArgsT, ResultT>(...consumerArgs);
      const rightPath = createRightPath<ArgsT, ResultT>(...consumerArgs);
      try {
        return rightPath
          .map(operationalFunc)
          .tap(() => {
            if (errorsOnly) return;
            onAction(name, { consumerArgs });
          })
          .getResult();
      } catch (e) {
        return leftPath
          .tap(() => onError(e as Error, name, { consumerArgs }))
          .mapError(errorFunc)
          .getResult();
      }
    };
  };

  return {
    // @ts-ignore
    runWithReporting,
  };
};

const datadogReporter = createReporter(RUMHelpers.addRUMError, RUMHelpers.addRUMAction);

export { createReporter, datadogReporter };
